import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MiniTrend } from "@/components/recovery/mini-trend";
import { STATUS_ORDER, STATUS_SHAPES, STATUS_SHORT_LABELS, type StatusKey } from "@/lib/status";

// Static class strings (Tailwind can't see dynamically-built class names).
const STATUS_ON: Record<StatusKey, string> = {
  green: "border-status-green bg-status-green-bg text-status-green-text",
  yellow: "border-status-yellow bg-status-yellow-bg text-status-yellow-text",
  red: "border-status-red bg-status-red-bg text-status-red-text",
};
import {
  ApiError,
  addListItem,
  getList,
  logListItem,
  type ListDetail as ListDetailData,
} from "@/lib/api";
import { shortDate } from "@/lib/format";

export default function ListDetail() {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const [data, setData] = useState<ListDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [busyItem, setBusyItem] = useState<string | null>(null);

  async function load() {
    try {
      setData(await getList(id));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else if (e instanceof ApiError && e.status === 404) setError("List not found.");
      else setError("Couldn't load this list.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function log(itemId: string, status: StatusKey) {
    setBusyItem(itemId);
    try {
      await logListItem(id, { item_id: itemId, status });
      await load();
    } catch {
      setError("Couldn't save that. Please try again.");
    } finally {
      setBusyItem(null);
    }
  }

  async function addNewItem() {
    if (!name.trim()) return;
    try {
      await addListItem(id, name.trim());
      setName("");
      setAdding(false);
      await load();
    } catch {
      setError("Couldn't add the item.");
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-muted-foreground">{error ?? "Not available."}</p>
        <Button variant="outline" onClick={() => navigate("/lists")}>
          Back to Lists
        </Button>
      </div>
    );
  }

  return (
    <div className="pb-16">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate("/lists")}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <h1 className="flex-1 text-lg font-semibold text-foreground">{data.list.name}</h1>
      </div>

      <div className="flex flex-col gap-3 px-6 pt-2">
        <Card>
          <CardContent className="px-4 py-3.5">
            <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
              Last 14 Days
            </p>
            <MiniTrend data={data.trend} className="mt-2.5" barClassName="h-7" />
            <div className="mt-1.5 flex justify-between">
              <span className="text-[11px] text-muted-foreground">
                {shortDate(shift(data.trend.length))}
              </span>
              <span className="text-[11px] text-muted-foreground">Today</span>
            </div>
          </CardContent>
        </Card>

        <p className="mt-1 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
          Today's Log
        </p>
        {data.items.length === 0 && !adding && (
          <p className="text-sm text-muted-foreground">
            No items yet. Add the things you want to track in this list.
          </p>
        )}
        {data.items.map((it) => {
          const logged = data.today_log[it.id]?.status;
          return (
            <Card key={it.id}>
              <CardContent className="flex flex-col gap-2.5 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-foreground">{it.name}</p>
                  {busyItem === it.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" strokeWidth={2} />}
                </div>
                <div className="flex gap-2">
                  {STATUS_ORDER.map((s) => {
                    const Shape = STATUS_SHAPES[s];
                    const on = logged === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        aria-label={STATUS_SHORT_LABELS[s]}
                        aria-pressed={on}
                        disabled={busyItem === it.id}
                        onClick={() => log(it.id, s)}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border-2 py-2 text-xs font-medium transition-colors ${
                          on ? STATUS_ON[s] : "border-border text-muted-foreground"
                        }`}
                      >
                        <Shape className="h-3.5 w-3.5" strokeWidth={2} />
                        {STATUS_SHORT_LABELS[s]}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {adding ? (
          <Card>
            <CardContent className="flex flex-col gap-2 p-3">
              <Input
                autoFocus
                placeholder="Add an item (e.g. Loneliness)"
                value={name}
                maxLength={80}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addNewItem()}
              />
              <div className="flex gap-2">
                <Button disabled={!name.trim()} onClick={addNewItem}>
                  Add
                </Button>
                <Button variant="ghost" onClick={() => { setAdding(false); setName(""); }}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Button variant="outline" fullWidth onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" strokeWidth={2} />
            Add Item
          </Button>
        )}
      </div>
    </div>
  );
}

/** Roughly the start-of-window date label (n days back from today, local). */
function shift(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - (n - 1));
  return d.toISOString().slice(0, 10);
}
